import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts } from '../services/productService';
import * as wishlistService from '../services/wishlistService';
import toast from 'react-hot-toast';
import getOptimizedUrl from '../utils/imageUtils';

export default function SearchSidebar() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  const normalize = (value) => String(value || '').toLowerCase().trim();
  const tokenize = (value) => normalize(value).split(/[^a-z0-9]+/).filter(Boolean);

  useEffect(() => {
    const onToggle = (e) => {
      const open = e?.detail?.open ?? !isOpen;
      if (open) {
        setMounted(true);
        setClosing(false);
        setIsOpen(true);
        loadProducts();
      } else {
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => {
          setMounted(false);
          setClosing(false);
          setSearchQuery('');
        }, 300);
      }
    };

    window.addEventListener('search:toggle', onToggle);
    return () => window.removeEventListener('search:toggle', onToggle);
  }, [isOpen]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await getProducts({});
      setProducts(Array.isArray(data) ? data : []);
      
      // Load wishlist
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (user && token) {
        try {
          const wishlistData = await wishlistService.getWishlist();
          setWishlistIds(wishlistData.map(item => item.product._id));
        } catch (error) {
          console.error('Failed to load wishlist:', error);
        }
      }
    } catch (error) {
      console.error('Search error', error);
      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) toast.error('Search failed');
    }
    setLoading(false);
  };

  const colorPrintTerms = useMemo(() => {
    const terms = new Set();
    products.forEach((product) => {
      tokenize(product.color).forEach((t) => terms.add(t));
      tokenize(product.print).forEach((t) => terms.add(t));
    });
    return terms;
  }, [products]);

  const filteredProducts = products.filter((product) => {
    const queryTerms = tokenize(searchQuery);
    if (!queryTerms.length) return false;

    const colorTermsInQuery = queryTerms.filter((term) => colorPrintTerms.has(term));
    const nonColorTerms = queryTerms.filter((term) => !colorPrintTerms.has(term));

    const colorPrintText = `${normalize(product.color)} ${normalize(product.print)}`;
    const colorIntentMatch =
      colorTermsInQuery.length === 0 ||
      colorTermsInQuery.every((term) => colorPrintText.includes(term));

    const searchableText = [
      normalize(product.product),
      normalize(product.category),
      normalize(product.color),
      normalize(product.print),
      normalize(product.fabricType),
      normalize(product.fabricPattern),
      normalize(product.productType),
      normalize(product.material),
      normalize(product.sizeAndFit),
      normalize(product.sleeves),
      normalize(product.closure),
      normalize(product.seoKeywords),
      (product.features || []).map((f) => normalize(f)).join(' '),
    ].join(' ');

    const nonColorMatch =
      nonColorTerms.length === 0 ||
      nonColorTerms.every((term) => searchableText.includes(term));

    // If query contains color/print terms (e.g. "red"), enforce them strictly.
    return colorIntentMatch && nonColorMatch;
  });

  const handleProductClick = (product) => {
    setClosing(true);
    setIsOpen(false);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
      setSearchQuery('');
    }, 300);
    navigate(`/product/${product.slug || product._id || product.id}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const toggleWishlist = async (productId, sizeOrEvent, maybeEvent) => {
    let size = undefined;
    let e = maybeEvent;
    if (sizeOrEvent && typeof sizeOrEvent === 'object' && sizeOrEvent.nativeEvent) {
      e = sizeOrEvent;
    } else {
      size = sizeOrEvent;
    }
    if (e) e.stopPropagation();
    
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!user || !token) {
      toast.error('Please login to add items to wishlist');
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", {
            detail: {
              open: true,
              returnToCurrent: true,
              returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          })
        );
      } catch (err) {}
      return;
    }
    
    const isCurrentlyInWishlist = wishlistIds.includes(productId);
    
    try {
      if (isCurrentlyInWishlist) {
        await wishlistService.removeFromWishlist(productId, size);
        const newWishlistIds = wishlistIds.filter(id => id !== productId);
        setWishlistIds(newWishlistIds);
        toast.success('Removed from wishlist');
        const data = await wishlistService.getWishlist();
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: data } }));
      } else {
        await wishlistService.addToWishlist(productId, size);
        setWishlistIds([...wishlistIds, productId]);
        toast.success('Added to wishlist!');
        const data = await wishlistService.getWishlist();
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: data } }));
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      if (error.response?.data?.message) {
        const backendMsg = error?.response?.data?.message;
        if (!backendMsg) toast.error('Search failed');
      } else {
        toast.error('Failed to update wishlist');
      }
    }
  };

  return (
    <>
      {mounted && (
        <>
          <div
            className="fixed inset-0 bg-black/40 font-bdogrotesk z-[10000]"
            onClick={() => {
              setClosing(true);
              setIsOpen(false);
              setTimeout(() => {
                setMounted(false);
                setClosing(false);
                setSearchQuery('');
              }, 300);
            }}
          />

          <div
            className={`fixed top-0 left-0 bottom-0 w-full max-w-lg bg-white z-[10001] shadow-2xl flex flex-col drawer-panel-left ${
              closing ? 'drawer-slide-out-left' : 'drawer-slide-in-left'
            }`}
          >
            <div className="px-4 py-2 flex items-center justify-between">
              <h3 className="text-base font-medium text-black font-bdogrotesk">
                Search
              </h3>
              <button
                onClick={() => {
                  setClosing(true);
                  setIsOpen(false);
                  setTimeout(() => {
                    setMounted(false);
                    setClosing(false);
                    setSearchQuery('');
                  }, 300);
                }}
                className="text-base font-medium text-black font-bdogrotesk"
              >
                Close
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 pb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder=""
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full bg-white border rounded-none border-black p-3 pr-10 text-base focus:ring-0 focus:outline-none font-bdogrotesk"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black text-xl font-bdogrotesk"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Products List */}
            <div className="p-4 flex-1 overflow-y-auto no-scrollbar">
              {loading ? (
                <div className="text-center text-black py-48 font-bold text-2xl font-bdogrotesk">
                  ...
                </div>
              ) : searchQuery === '' ? (
                <div className="text-center text-gray-500 py-12 text-sm font-bdogrotesk">
                  Start typing to search products...
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center text-gray-500 py-12 text-sm font-bdogrotesk">
                  No products found for "{searchQuery}"
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredProducts.map((product) => {
                    const discountPercent = product.mrp && product.discountedPrice 
                      ? Math.round(((product.mrp - product.discountedPrice) / product.mrp) * 100)
                      : 0;
                    
                    return (
                      <div key={product._id}>
                        <div
                          className="flex gap-4 items-start cursor-pointer"
                          onClick={() => handleProductClick(product)}
                        >
                          {/* Product Image */}
                          <div 
                            className="w-40 h-48 bg-white flex-shrink-0 overflow-hidden cursor-pointer"
                          >
                              {product.images?.[0] ? (
                              <img
                                src={getOptimizedUrl(product.images[0] || '', 800)}
                                alt={product.product}
                                className="w-full h-full object-cover bg-white"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                No Image
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 flex flex-col min-h-[12rem]">
                            <div className="flex justify-between items-start">
                              <div className="max-w-[220px]">
                                <div className="font-medium font-bdogrotesk text-base">
                                  {product.product}
                                </div>
                                
                                {/* Shop Now Button */}
                                <button
                                  onClick={() => handleProductClick(product)}
                                  className="mt-4 px-4 py-1 border-2 font-bdogrotesk border-solid border-black text-sm hover:bg-black hover:text-white transition-colors"
                                >
                                  Shop Now
                                </button>
                              </div>
                              
                              {/* Wishlist Heart */}
                              <button
                                onClick={(e) => toggleWishlist(product._id, e)}
                                className="shrink-0 pr-2 p-2"
                              >
                                {wishlistIds.includes(product._id) ? (
                                  <svg
                                    width="26"
                                    height="26"
                                    viewBox="0 0 19 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                      fill="#F31717"
                                      stroke="#F31717"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="26"
                                    height="26"
                                    viewBox="0 0 19 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                                      fill="white"
                                      stroke="black"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                            
                            {/* Price at bottom */}
                            <div className="mt-auto flex items-end gap-2 pb-1">
                              {product.mrp && product.discountedPrice && product.mrp > product.discountedPrice && (
                                <div className="text-sm text-gray-400 line-through">
                                  {formatPrice(product.mrp)}
                                </div>
                              )}
                              <div className="text-sm font-semibold">
                                {formatPrice(product.discountedPrice || product.mrp)}
                                {discountPercent > 0 && (
                                  <span className="text-red-500 pl-1 text-xs">
                                    ({discountPercent}% Off)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr className="my-4 border-black" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </>
      )}
    </>
  );
}
